import { PlusOutlined, WarningOutlined } from "@ant-design/icons";
import {
  Spin,
  Row,
  Col,
  Button,
  Tooltip,
  Typography,
  Divider,
  Modal,
  List,
} from "antd";
import IconInfinite from "assets/images/infinite";
import { Fragment } from "react/jsx-runtime";
import DistributionPointTable from "./components/DistributionPointTable";
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  DistributionPointResponseResource,
  getPageableDistributionPoints,
  entitlementToRegister,
  getSubscription,
  SubscriptionDetails,
} from "api/generated";
import "../../routes/register/styles.css";
import { useClient } from "hooks/useClient";
import { CV_CONTEXT_PATH, getIsComanyAdministrator } from "security";
import { RouteComponentProps, withRouter } from "react-router-dom";

function DistributionPointScreen({ history }: RouteComponentProps) {
  const { client } = useClient();
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [spinnerSubscription, setSpinnerSubscription] = useState(false);

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [sort, setSort] = useState<string[]>(["id,DESC"]);
  const [filters, setFilters] = useState("");
  const [filterEntitled, setFilterEntitled] = useState<boolean>();

  const [distributionPoints, setDistributionPoints] = useState<
    DistributionPointResponseResource[]
  >([]);
  const [subscription, setSubscription] = useState<SubscriptionDetails>();

  const isCompanyAdministrator: boolean = useMemo(() => {
    return getIsComanyAdministrator();
  }, []);

  const isSubscriptionOnFreeMode = useMemo(() => {
    return (
      subscription?.subscriptionMessages &&
      subscription?.subscriptionMessages.find(
        (x) => x === "SUBSCRIPTIONS_ON_FREE_MODE"
      )
    );
  }, [subscription]);

  // Función para obtener puntos de distribución
  const fetchDistributionPoints = useCallback(async () => {
    setLoadingSpinner(true);
    try {
      const { data } = await getPageableDistributionPoints({
        client,
        query: {
          textFilter: filters,
          entitled: filterEntitled,
          pageRequest: {
            page: currentPage,
            size: pageSize,
            sort: sort,
          },
        },
        // Serializador para manejar arrays anidados
        querySerializer: (params: Record<string, unknown>) => {
          const searchParams = new URLSearchParams();

          Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              if (key === "pageRequest") {
                // Manejar el objeto recursivamente
                Object.entries(value).forEach(([subKey, subValue]) => {
                  if (subValue !== null && subValue !== undefined) {
                    if (subKey === "sort" && Array.isArray(subValue)) {
                      // Para el array sort, unir elementos con comas
                      searchParams.append(`${subKey}`, subValue.join(","));
                    } else {
                      searchParams.append(`${subKey}`, String(subValue));
                    }
                  }
                });
              } else {
                searchParams.append(key, String(value));
              }
            }
          });

          return searchParams.toString();
        },
      });

      setTotalItems(data?.totalElements ?? 0);
      setDistributionPoints(data?.content ?? []);
    } catch (error) {
      console.error("Error fetching distribution points:", error);
    } finally {
      setLoadingSpinner(false);
    }
  }, [client, currentPage, pageSize, sort, filters, filterEntitled]);

  // Función para obtener la suscripción
  const fetchSubscription = useCallback(async () => {
    try {
      const { data } = await getSubscription({ client });
      setSubscription(data);
    } catch (error) {
      console.error("Error fetching subscription:", error);
    }
  }, [client]);

  function showNewDistributionPointForm() {
    setSpinnerSubscription(true);
    entitlementToRegister({ client })
      .then(({ data }) => {
        if (data?.entitlement) {
          history.push("distributionPoints/new");
        } else {
          Modal.error({
            title: "Suscripción: Excepción al registrar punto de distribución",
            icon: <WarningOutlined />,
            content: (
              <List>
                {data?.subscriptionDetails?.subscriptionMessageList?.map(
                  (error) => {
                    return <List.Item key={error}>{error}</List.Item>;
                  }
                )}
              </List>
            ),
            onOk: () => {
              window.location.replace(`${CV_CONTEXT_PATH}/subscription`);
            },
          });
        }
      })
      .finally(() => {
        setSpinnerSubscription(false);
      });
  }

  // Función para manejar cambios en la tabla
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    console.log(filters);
    const { current, pageSize } = pagination;
    const { entitled } = filters;

    setCurrentPage(current - 1);
    setPageSize(pageSize);

    if (entitled && entitled.length === 1) {
      setFilterEntitled(entitled[0]);
    } else {
      setFilterEntitled(undefined);
    }

    // Manejar el sort
    if (sorter.field) {
      const direction = sorter.order?.includes("asc") ? "ASC" : "DESC";
      const field = sorter.field === "subsidiary" ? "name" : sorter.field;
      setSort([`${field},${direction}`]);
    } else {
      setSort(["id,DESC"]);
    }
  };

  // Función para refrescar todos los datos
  const refreshData = useCallback(() => {
    fetchSubscription();
    fetchDistributionPoints();
  }, [fetchSubscription, fetchDistributionPoints]);

  // Efectos iniciales
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  useEffect(() => {
    fetchDistributionPoints();
  }, [fetchDistributionPoints]);

  return (
    <Fragment>
      <Spin size="large" tip="Cargando..." spinning={loadingSpinner}>
        <div>
          <div style={{ marginBottom: "24px" }}>
            <Row>
              <Col lg={12} xs={24}>
                <Button
                  hidden={!isCompanyAdministrator}
                  className="customized-primary"
                  type="primary"
                  icon={<PlusOutlined />}
                  loading={spinnerSubscription}
                  onClick={showNewDistributionPointForm}
                >
                  Agregar Punto de Distribución
                </Button>
              </Col>
              <Col
                lg={12}
                xs={24}
                style={{
                  display: "flex",
                  placeContent: "flex-end",
                }}
              >
                <Tooltip title="Suscripción">
                  <Typography.Title code level={3}>
                    <Typography.Text style={{ fontSize: "17px" }}>
                      Puntos de Distribución &nbsp;
                    </Typography.Text>
                    {subscription?.activeDistributionPoints}/
                    {isSubscriptionOnFreeMode ? (
                      <IconInfinite />
                    ) : (
                      subscription?.equipmentsNumber
                    )}
                  </Typography.Title>
                </Tooltip>
              </Col>
            </Row>
          </div>
          <Row gutter={24}>
            <DistributionPointTable
              distributionPointList={distributionPoints}
              refreshOnUpdate={refreshData}
              pagination={{
                current: currentPage + 1,
                pageSize: pageSize,
                total: totalItems,
              }}
              handleTableChange={handleTableChange}
              handleSearchChange={(value: string) => {
                setCurrentPage(0);
                setFilters(value);
              }}
            />
          </Row>
        </div>

        <Divider />
      </Spin>
    </Fragment>
  );
}

export default withRouter(DistributionPointScreen);
